<script lang="ts" setup>
import type { FetchError } from 'ofetch'
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicInput from '~/components/form-elements/BasicInput.vue'
import PhoneNumber from '~/components/form-elements/PhoneNumber.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import AfterSupplierRegistration from '~/components/modal-windows/supplier/AfterSupplierRegistration.vue'
import IndividualEntrepreneur from '~/components/modal-windows/supplier/forms/IndividualEntrepreneur.vue'
import LlcOrPtc from '~/components/modal-windows/supplier/forms/LlcOrPtc.vue'
import { LegalSubjectType, LegalSubjectTypeUtils } from '~/enums/legal-subject-type'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import { useAuthStore } from '~/store/auth'
import { useUserStore } from '~/store/user'
import type { RadioButtonChoice } from '~/types/form-elements'

const model = defineModel<boolean>()

const buttonTitle = 'Отправить заявку'
const emptyForm = {
  contactPersonData: {
    email: null,
    fullName: null,
    phoneNumber: null,
    telegram: null,
    whatsApp: null
  }
}
const legalSubjectTypeChoices = LegalSubjectTypeUtils.sortedMembersForSupplierApplication.map(
  (x: LegalSubjectType): RadioButtonChoice => ({ labelText: LegalSubjectTypeUtils.getShortName(x), value: x })
)
const messages = {
  failedToCreateSupplierApplication: 'Не удалось создать заявку на регистрацию поставщика.',
  failedToGetUser: 'Не удалось получить данные пользователя.'
}

const { $apiHelper, $toast } = useNuxtApp()
const authStore = useAuthStore()
const userStore = useUserStore()

const form = ref<any>({ ...emptyForm })
const hasUnsavedChanges = ref(false)
const isLoading = ref(false)
const legalSubjectType = ref(LegalSubjectType.LimitedLiabilityCompany)

const individualEntrepreneurForm = ref(null)
const llcOrPtcForm = ref(null)

const email = ref(null)
const fullName = ref(null)
const phoneNumberRef = ref(null)

const currentForm = computed(() => {
  const { IndividualEntrepreneur, LimitedLiabilityCompany, PubliclyTradedCompany } = LegalSubjectType

  switch (legalSubjectType.value) {
    case IndividualEntrepreneur:
      return individualEntrepreneurForm.value
    case LimitedLiabilityCompany:
    case PubliclyTradedCompany:
      return llcOrPtcForm.value
    default:
      return null
  }
})

async function loadUserData (): Promise<void> {
  try {
    const { data } = await $apiHelper.users.getUser(authStore.userId!)

    userStore.updateFromUserData(data)
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetUser)
  }
}

function close (): void {
  model.value = false
}

async function saveChanges (): Promise<void> {
  isLoading.value = true

  const errors = currentForm.value.validateAll()

  const contactPersonRefs = [fullName, phoneNumberRef, email]

  for (const x of contactPersonRefs) {
    const error = helpers.validateField(x.value)

    if (error) {
      errors.push(error)
    }
  }

  if (errors.length) {
    for (const x of errors) {
      $toast.error(x)
    }
  } else {
    const data = Object.assign(
      {},
      currentForm.value.getData(),
      form.value,
      { legalSubjectTypeId: legalSubjectType.value }
    )

    try {
      await $apiHelper.supplierApplications.createSupplierApplication(data)

      hasUnsavedChanges.value = false

      close()

      await loadUserData()

      await openAfterSupplierRegistrationModalWindow()
    } catch (error) {
      ApiErrorHandler.handleFetchError(error as FetchError, messages.failedToCreateSupplierApplication)
    }
  }

  isLoading.value = false
}

async function openAfterSupplierRegistrationModalWindow (): Promise<void> {
  await useModal({ component: AfterSupplierRegistration }).open()
}
</script>

<template>
  <CustomModalWindow v-model="model" :has-unsaved-changes="hasUnsavedChanges" prevent-unsaved-changes size="large">
    <template #title>
      Регистрация поставщика
    </template>

    <Notification class="mb-6" is-small status="info">
      Поля, отмеченные звёздочкой, обязательны для заполнения.
    </Notification>

    <h4>Сведения о&nbsp;компании</h4>

    <Notification class="mb-6" is-small status="info">
      Поставщиком может стать только ООО, ОАО или ИП.
    </Notification>

    <form class="mb-6" @change="hasUnsavedChanges = true" @submit.prevent>
      <RadioButtons
        v-model="legalSubjectType"
        :choices="legalSubjectTypeChoices"
        class="mb-6"
        is-required
        label-text="Тип занятости"
        name="legalSubjectType"
      />

      <LlcOrPtc
        v-if="
          legalSubjectType === LegalSubjectType.LimitedLiabilityCompany
            || legalSubjectType === LegalSubjectType.PubliclyTradedCompany
        "
        ref="llcOrPtcForm"
        class="mb-6"
      />
      <IndividualEntrepreneur
        v-else-if="legalSubjectType === LegalSubjectType.IndividualEntrepreneur"
        ref="individualEntrepreneurForm"
        class="mb-6"
      />

      <hr class="mb-6">

      <h4>Сведения о&nbsp;контактном лице</h4>

      <Notification class="mb-6" is-small status="info">
        Контактное лицо&nbsp;&mdash; это человек, ответственный за&nbsp;принятие решения и&nbsp;поставки продуктов.
      </Notification>

      <BasicInput
        ref="fullName"
        v-model="form.contactPersonData.fullName"
        class="mb-6"
        is-required
        label-text="Ф. И. О. (полностью)"
        :max-length="255"
      />

      <div class="row mb-6">
        <div class="column-6 tb12 with-margin-bottom-24">
          <PhoneNumber
            ref="phoneNumberRef"
            v-model="form.contactPersonData.phoneNumber"
            is-only-one-country
            is-required
            label-text="Номер телефона"
          />
        </div>
        <div class="column-6 tb12">
          <BasicInput
            ref="email"
            v-model="form.contactPersonData.email"
            is-required
            label-text="Email"
            :max-length="255"
          />
        </div>
      </div>

      <div class="row mb-6">
        <div class="column-6 tb12 with-margin-bottom-24">
          <BasicInput v-model="form.contactPersonData.telegram" label-text="Telegram" :max-length="255" />
        </div>
        <div class="column-6 tb12">
          <BasicInput v-model="form.contactPersonData.whatsApp" label-text="WhatsApp" :max-length="255" />
        </div>
      </div>
    </form>

    <BasicButton class="mb-6" color="green" :is-loading="isLoading" :title="buttonTitle" @click="saveChanges" />

    <Agreement :button-title="buttonTitle" need-to-agree-with-offer-for-suppliers />
  </CustomModalWindow>
</template>
